import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoRefreshToken,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

// ---------------------------------------------------------------------------
// Pool configuration (from env vars)
// ---------------------------------------------------------------------------

function getUserPool(): CognitoUserPool {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;

  if (!userPoolId || !clientId) {
    throw new Error(
      "Missing Cognito env vars: VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID are required",
    );
  }

  return new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

export type InitiateAuthError =
  | { type: "USER_NOT_FOUND" }
  | { type: "TOO_MANY_REQUESTS" }
  | { type: "UNEXPECTED"; message: string };

export type RespondToChallengeError =
  | { type: "INCORRECT_CODE" }
  | { type: "EXPIRED_CODE" }
  | { type: "NOT_AUTHORIZED" }
  | { type: "UNEXPECTED"; message: string };

export type SignUpError =
  | { type: "USER_EXISTS" }
  | { type: "UNEXPECTED"; message: string };

export interface InitiateAuthResult {
  session: CognitoUser;
}

// ---------------------------------------------------------------------------
// initiateAuth — step 1: send email, trigger CUSTOM_AUTH challenge
// ---------------------------------------------------------------------------

export async function initiateAuth(email: string): Promise<
  | { ok: true; cognitoUser: CognitoUser }
  | { ok: false; error: InitiateAuthError }
> {
  const pool = getUserPool();
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });

  const authDetails = new AuthenticationDetails({
    Username: email,
  });

  return new Promise((resolve) => {
    cognitoUser.initiateAuth(authDetails, {
      onSuccess: () => {
        // This shouldn't happen at step 1 — CUSTOM_AUTH always challenges first
        resolve({ ok: true, cognitoUser });
      },
      onFailure: (err: { code?: string; message?: string }) => {
        if (err.code === "UserNotFoundException") {
          resolve({ ok: false, error: { type: "USER_NOT_FOUND" } });
        } else if (err.code === "TooManyRequestsException" || err.code === "LimitExceededException") {
          resolve({ ok: false, error: { type: "TOO_MANY_REQUESTS" } });
        } else {
          resolve({
            ok: false,
            error: { type: "UNEXPECTED", message: err.message ?? "Unknown error" },
          });
        }
      },
      customChallenge: () => {
        // OTP challenge received — user must now submit the code
        resolve({ ok: true, cognitoUser });
      },
    });
  });
}

// ---------------------------------------------------------------------------
// signUp — register a new candidate (auto-confirmed by Pre Sign-up Lambda)
// ---------------------------------------------------------------------------

export async function signUp(
  email: string,
): Promise<{ ok: true } | { ok: false; error: SignUpError }> {
  const pool = getUserPool();
  // Password is required by Cognito API but never used (CUSTOM_AUTH is passwordless)
  const password = crypto.randomUUID() + "Aa1!";

  return new Promise((resolve) => {
    pool.signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: "email", Value: email })],
      [],
      (err) => {
        if (err) {
          if (err.name === "UsernameExistsException") {
            resolve({ ok: false, error: { type: "USER_EXISTS" } });
          } else {
            resolve({
              ok: false,
              error: { type: "UNEXPECTED", message: err.message },
            });
          }
          return;
        }
        resolve({ ok: true });
      },
    );
  });
}

// ---------------------------------------------------------------------------
// respondToChallenge — step 2: submit OTP code
// ---------------------------------------------------------------------------

export async function respondToChallenge(
  cognitoUser: CognitoUser,
  code: string,
): Promise<{ ok: true; tokens: CognitoTokens } | { ok: false; error: RespondToChallengeError }> {
  return new Promise((resolve) => {
    cognitoUser.sendCustomChallengeAnswer(code, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();
        const expiresAt = session.getIdToken().getExpiration() * 1000;

        resolve({
          ok: true,
          tokens: { idToken, accessToken, refreshToken, expiresAt },
        });
      },
      onFailure: (err: { code?: string; message?: string }) => {
        if (err.code === "NotAuthorizedException") {
          // Can mean expired session or max attempts exceeded
          const msg = err.message?.toLowerCase() ?? "";
          if (msg.includes("expired")) {
            resolve({ ok: false, error: { type: "EXPIRED_CODE" } });
          } else {
            resolve({ ok: false, error: { type: "NOT_AUTHORIZED" } });
          }
        } else if (err.code === "CodeMismatchException") {
          resolve({ ok: false, error: { type: "INCORRECT_CODE" } });
        } else {
          resolve({
            ok: false,
            error: { type: "UNEXPECTED", message: err.message ?? "Unknown error" },
          });
        }
      },
      customChallenge: () => {
        // Wrong code but more attempts remaining
        resolve({ ok: false, error: { type: "INCORRECT_CODE" } });
      },
    });
  });
}

// ---------------------------------------------------------------------------
// refreshSession — exchange a refresh token for new Cognito tokens
// ---------------------------------------------------------------------------

export type RefreshSessionError =
  | { type: "NO_REFRESH_TOKEN" }
  | { type: "REFRESH_FAILED"; message: string };

export async function refreshSession(
  email: string,
  refreshToken: string,
): Promise<{ ok: true; tokens: CognitoTokens } | { ok: false; error: RefreshSessionError }> {
  if (!refreshToken) {
    return { ok: false, error: { type: "NO_REFRESH_TOKEN" } };
  }

  const pool = getUserPool();
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

  return new Promise((resolve) => {
    cognitoUser.refreshSession(token, (err, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve({
          ok: false,
          error: {
            type: "REFRESH_FAILED",
            message: (err as { message?: string } | null)?.message ?? "Refresh failed",
          },
        });
        return;
      }

      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const newRefreshToken = session.getRefreshToken().getToken();
      const expiresAt = session.getIdToken().getExpiration() * 1000;

      resolve({
        ok: true,
        tokens: { idToken, accessToken, refreshToken: newRefreshToken, expiresAt },
      });
    });
  });
}
